import { useLayoutEffect, useRef } from 'react';
import { Button, Icon } from './primitives';

export interface UpdateBarProps {
  /** A sentence the screen on show adds — the playground's, while a turn is running. */
  note?: string | null;
  /** The reload has started and the page is on its way out. */
  reloading?: boolean;
  onReload: () => void;
}

/**
 * The strip that says a newer BoardFlight is deployed than the one in this tab (`src/update.ts`).
 *
 * **Across the top and in the flow of the page, rather than floating over a corner.** The
 * playground's windows are wherever somebody dragged them, so there is no corner that is not
 * somebody's composer, and a notice that cannot be dismissed must never sit on top of work — so it
 * pushes instead of covering. On the dashboard that is ordinary flow. The playground's screen is
 * fixed to the viewport and cannot be pushed, so the strip publishes its own height as
 * `--bf-update-offset` and the screen starts below it (`.pg-screen` in kit.css).
 *
 * **No close control, deliberately.** It is on the screen because the build in this tab may be the
 * one that was broken, and it goes away the only honest way: by the page being on the new build.
 */
export function UpdateBar({ note, reloading = false, onReload }: UpdateBarProps) {
  const bar = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = bar.current;
    if (!element) return;
    const root = document.documentElement;
    // Measured rather than assumed: the sentence wraps on a narrow window, and a screen offset by a
    // guessed height either hides the strip's last line or leaves a gap under it.
    const publish = () => root.style.setProperty('--bf-update-offset', `${element.offsetHeight}px`);
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(element);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--bf-update-offset');
    };
  }, []);

  return (
    <div ref={bar} className="bf-update" role="status">
      <Icon name="refresh" size={20} className="bf-update-icon" />
      <p className="bf-update-text">
        <span className="bf-update-title">A new version of BoardFlight is available.</span>
        {note ? <span className="bf-update-note">{note}</span> : null}
      </p>
      <Button variant="secondary" size="sm" disabled={reloading} onClick={onReload}>
        {reloading ? 'Reloading…' : 'Reload'}
      </Button>
    </div>
  );
}
